import { useEffect, useState } from 'react';
import { supabase } from './supabase';

/**
 * El canal por el que el despliegue avisa. El mensaje solo lleva el sello de la construcción.
 *
 * Privado del módulo: quien más lo nombra es `scripts/desplegar-pages.mjs` al emitir y
 * `scripts/sonda/plataforma.mjs` al vigilarlo, y ninguno de los dos puede importar de aquí.
 */
const CANAL_VERSION = 'tanteo-version';

/** Suelo: si el aviso del despliegue no llega, se pregunta igual una vez al día. */
const UN_DIA = 24 * 60 * 60 * 1000;

/** Cuándo se mira tras el aviso, en milisegundos. Cubre lo que tarda el archivo nuevo en servirse. */
const REINTENTOS = [0, 5_000, 15_000, 30_000, 60_000];

/**
 * Detecta una versión nueva con la pestaña abierta: el despliegue emite por `CANAL_VERSION`, y la
 * comprobación diaria es el suelo. Exige que `sw.js` cambie: el build le pone un sello.
 */
export function vigilarVersion(alHaberVersionNueva: () => void): () => void {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return () => {};

  const alCambiar = () => alHaberVersionNueva();
  navigator.serviceWorker.addEventListener('controllerchange', alCambiar);

  const comprobar = () => {
    if (!navigator.onLine) return;
    void navigator.serviceWorker.getRegistration().then((r) => r?.update()).catch(() => {});
  };
  const alVolver = () => {
    if (document.visibilityState === 'visible') comprobar();
  };

  // El aviso sale en cuanto termina el despliegue, y la red de entrega tarda unos segundos en
  // servir el archivo nuevo: mirar una sola vez se adelanta y no encuentra nada.
  const pendientes: number[] = [];
  const insistir = () => {
    for (const ms of REINTENTOS) pendientes.push(window.setTimeout(comprobar, ms));
  };

  const canal = supabase.channel(CANAL_VERSION).on('broadcast', { event: 'desplegado' }, insistir);
  void canal.subscribe();

  const reloj = window.setInterval(comprobar, UN_DIA);
  document.addEventListener('visibilitychange', alVolver);

  return () => {
    navigator.serviceWorker.removeEventListener('controllerchange', alCambiar);
    void supabase.removeChannel(canal);
    window.clearInterval(reloj);
    for (const id of pendientes) window.clearTimeout(id);
    document.removeEventListener('visibilitychange', alVolver);
  };
}

/** La bandera que pinta el aviso. La lógica vive en `vigilarVersion`, que se prueba sin React. */
export function useVersionNueva(): boolean {
  const [hay, setHay] = useState(false);
  useEffect(() => vigilarVersion(() => setHay(true)), []);
  return hay;
}
